import React from 'react';
import { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../icon';
import AngleRight from '../icons/angle-right-solid.svg';
import AngleLeft from '../icons/angle-left-solid.svg';

const defaultColorStyle = css<{ $type: TextButtonType }>`
    ${(props) =>
        props.$type === 'destructive'
            ? props.theme.app.component.textButton.destructive.default.color
            : props.theme.app.component.textButton.primary.default.color};
`;

const hoverColorStyle = css<{ $type: TextButtonType }>`
    ${(props) =>
        props.$type === 'destructive'
            ? props.theme.app.component.textButton.destructive.hover.color
            : props.theme.app.component.textButton.primary.hover.color};
`;

const activeColorStyle = css<{ $type: TextButtonType }>`
    ${(props) =>
        props.$type === 'destructive'
            ? props.theme.app.component.textButton.destructive.active.color
            : props.theme.app.component.textButton.primary.active.color};
`;

const Text = styled.span<{ $type: TextButtonType }>`
    color: ${defaultColorStyle};
    font-size: ${(props) => props.theme.app.component.textButton.fontSize};
    font-weight: ${(props) => props.theme.app.component.textButton.fontWeight};
`;

const AngleLeftStyled = styled(AngleLeft)<{ $type: TextButtonType }>`
    fill: ${defaultColorStyle};
`;

const AngleRightStyled = styled(AngleRight)<{ $type: TextButtonType }>`
    fill: ${defaultColorStyle};
`;

const StyledIcon = styled(Icon)`
    width: ${(props) => props.theme.app.component.textButton.iconWidth};
    height: ${(props) => props.theme.app.component.textButton.iconHeight};
`;

const TextButtonWrapper = styled.span<{ $type: TextButtonType }>`
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
        ${Text} {
            color: ${hoverColorStyle};
        }
        ${AngleLeftStyled} {
            fill: ${hoverColorStyle};
        }
        ${AngleRightStyled} {
            fill: ${hoverColorStyle};
        }
    }

    &:active {
        ${Text} {
            color: ${activeColorStyle};
        }
        ${AngleLeftStyled} {
            fill: ${activeColorStyle};
        }
        ${AngleRightStyled} {
            fill: ${activeColorStyle};
        }
    }
`;

type IconPosition = 'unset' | 'left' | 'right';
type TextButtonType = 'primary' | 'destructive';

interface Props {
    className?: string;
    iconPosition?: IconPosition;
    type?: TextButtonType;
    onClick?: () => void;
}

export const TextButton = ({ className, iconPosition = 'unset', type = 'primary', onClick, children }: PropsWithChildren<Props>) => {
    return (
        <TextButtonWrapper $type={type} className={className} onClick={onClick}>
            {iconPosition === 'left' && (
                <StyledIcon>
                    <AngleLeftStyled $type={type} />
                </StyledIcon>
            )}
            <Text $type={type}>{children}</Text>
            {iconPosition === 'right' && (
                <StyledIcon>
                    <AngleRightStyled $type={type} />
                </StyledIcon>
            )}
        </TextButtonWrapper>
    );
};
